import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppPaths from '../../../AppPaths';
import CheckoutValueProps from '../../../components/CheckoutValueProps';
import ImageGallery from '../../../components/ImageGallery/ImageGallery';
import SubscriptionOptions from '../../../components/SubscriptionOptions';
import { filterSixMonthPlanForExperiment } from '../../../components/SubscriptionOptions/hideSixMonthPlanTest';
import useSubscriptionProducts from '../../../hooks/useSubscriptionProducts';
import * as events from '../../../lib/analytics/events';
import { cartActions as storeCartActions } from '../../../reducers/storeCart';
import * as types from '../../../types';
import { series2CollarId } from '../../../types';
import AddToBagButton from '../components/AddToBagButton';
import BandVariantOptions from '../components/BandVariantOptions';
import ProductTitle from '../components/ProductTitle';
import SoldOutBanner from '../components/SoldOutBanner';
import useIsSkuBlocked from '../hooks/useIsSkuBlocked';
import useSubscriptionSelector from '../hooks/useSubscriptionSelector';
import useVariantSelector from '../hooks/useVariantSelector';
import styles from '../ProductDetails.module.scss';
import ProductFeatures from './components/ProductFeatures';
import SubItems from './components/SubItems';
import { useLocale } from '../../../contexts/LocaleContext';

interface OnChangeHandlerProps {
  priceInCents: number;
  productName: string;
  currency: string;
}

function useOnChangeHandlers({ priceInCents, productName, currency }: OnChangeHandlerProps) {
  const onColorChange = useCallback(
    (newVariant: types.IVariant) => {
      events.series2CollarColorSwitch({
        color: newVariant.options.color,
        priceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        currency,
      });
    },
    [priceInCents, productName, currency],
  );

  const onSizeChange = useCallback(
    (newVariant: types.IVariant) => {
      events.series2CollarSizeSwitch({
        color: newVariant.options.color,
        priceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        currency,
      });
    },
    [priceInCents, productName, currency],
  );

  const onSelectedVariantChange = useCallback(
    (newVariant: types.IVariant) => {
      events.series2CollarVariantSwitch({
        color: newVariant.options.color,
        priceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        currency,
      });
    },
    [priceInCents, productName, currency],
  );

  return {
    onColorChange,
    onSizeChange,
    onSelectedVariantChange,
  };
}

function selectedVariantImageUrls(variant: types.IVariant): string[] {
  const lifestyleImageDirectory = `/lifestyle_images/smart-collar-v2/${variant.options.color}`;
  return [
    `/product_images/smart-collar-v2/${variant.options.color}.jpg`,
    `${lifestyleImageDirectory}/01.jpg`,
    `${lifestyleImageDirectory}/02.jpg`,
    `${lifestyleImageDirectory}/03.jpg`,
    `${lifestyleImageDirectory}/04.jpg`,
  ];
}

interface SubscriptionOptionsProps {
  onSubscriptionChange: (sku: string | undefined) => void;
  selectedSubscriptionSku: string | undefined;
  productName: string;
}

function Series2SubscriptionOptions({
  onSubscriptionChange,
  selectedSubscriptionSku,
  productName,
}: SubscriptionOptionsProps) {
  const subscriptionProducts = filterSixMonthPlanForExperiment(useSubscriptionProducts(series2CollarId));
  return (
    <SubscriptionOptions
      allowNoSubscription
      onSubscriptionChange={onSubscriptionChange}
      selectedSubscriptionSku={selectedSubscriptionSku}
      subscriptionProducts={subscriptionProducts}
      productName={productName}
      startAsUnselected={false}
      disableSection={false}
    />
  );
}

interface Series2CollarDetailsProps {
  priceInCents: number;
  productName: string;
  variants: types.IVariant[];
}

export default function Series2CollarDetails({ priceInCents, productName, variants }: Series2CollarDetailsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { currency } = useLocale();

  const { onColorChange, onSizeChange, onSelectedVariantChange } = useOnChangeHandlers({
    priceInCents,
    productName,
    currency,
  });

  const { changeVariantOptions, selectedVariant } = useVariantSelector({
    onColorChange,
    onSelectedVariantChange,
    onSizeChange,
    variants,
    useQueryParams: true,
  });
  const isBlocked = useIsSkuBlocked(selectedVariant.sku);

  const subscriptionProducts = filterSixMonthPlanForExperiment(useSubscriptionProducts(series2CollarId));
  const { selectedSubscription, selectSubscriptionSku } = useSubscriptionSelector(subscriptionProducts);

  const valueProps = [t('free_shipping')];

  useEffect(() => {
    events.bandViewed();
  }, []);

  return (
    <>
      <div className={styles.productContainer}>
        <div className={styles.imageGalleryContainer}>
          <ImageGallery
            media={selectedVariantImageUrls(selectedVariant).map((url) => ({
              type: 'image',
              url,
              retinaWidth: 612,
              retinaHeight: 612,
            }))}
          />
        </div>
        <div className={styles.productDetails}>
          <div className={styles.productDetailsHeader}>
            <div className={styles.productDetailsTitle}>
              <ProductTitle productName={productName} price={{ priceInCents }} />
            </div>
            <CheckoutValueProps valueProps={valueProps} />
            <p className={styles.description}>
              {t(
                'chew_proof_water_proof_and_generally_dog_proof_bluetooth_and_wi_fi_enabled_to_track_your_walks_and_activity_and_keep_your_dog_in_good_shape_charging_accessories_included',
              )}
            </p>
          </div>

          <BandVariantOptions
            bandSeries={types.BandSeries.F1}
            changeVariantOptions={changeVariantOptions}
            selectedVariant={selectedVariant}
            variants={variants}
            startSizeAsUnselected={false}
          />

          {isBlocked && <SoldOutBanner />}

          <Series2SubscriptionOptions
            onSubscriptionChange={selectSubscriptionSku}
            selectedSubscriptionSku={selectedSubscription?.sku}
            productName={productName}
          />

          <div className={styles.productAction}>
            <AddToBagButton
              disabled={isBlocked}
              onAddToBag={() => {
                if (isBlocked) {
                  return false;
                }

                events.series2CollarAddedToBag({
                  color: selectedVariant.options.color,
                  priceInCents,
                  productName,
                  size: selectedVariant.options.size,
                  sku: selectedVariant.sku,
                  currency,
                });

                if (selectedSubscription) {
                  events.series2SubscriptionAddedToBag({
                    priceInCents: selectedSubscription.priceInCents,
                    productId: selectedSubscription.id,
                    productName: selectedSubscription.name,
                    sku: selectedSubscription.sku,
                    currency,
                  });

                  dispatch(
                    storeCartActions.addBundleCartItem({
                      sku: selectedVariant.sku,
                      subscriptionSku: selectedSubscription.sku,
                      currency,
                    }),
                  );
                } else {
                  dispatch(storeCartActions.addSingleLineCartItem({ sku: selectedVariant.sku, currency }));
                }

                history.push(AppPaths.Bag);

                return true;
              }}
            />
          </div>
        </div>
      </div>

      {selectedVariant && <SubItems variant={selectedVariant} />}
      <ProductFeatures />
    </>
  );
}
